// src/components/weopen/WeOpenOwnershipControls.tsx
//
// Explicit lifecycle actions for a WeOpen conversation:
//   Phase 4 PR 2 — Claim, Release-to-queue
//   Phase 4 PR 3 — Close, with an optional closure summary
//
// ── CLAIM IS ALWAYS A DELIBERATE ACT ────────────────────────────────────────
//
// Nothing here fires on open, select, focus, typing or send. Phase 4 PR 1 exists
// precisely so that replying cannot acquire ownership, and an automatic claim
// would reintroduce that by the back door — an agent who merely opened a
// conversation to read it would find themselves holding it, having consumed a slot
// of their own capacity.
//
// ── WEOPEN DECIDES; THIS RENDERS ────────────────────────────────────────────
//
// Which control appears is presentation. Authorization is WeOpen's:
//
//   404  not found, and also what an out-of-scope caller sees
//   403  ROUTING_SCOPE_FORBIDDEN — routed to a different team
//   403  NOT_CONVERSATION_OWNER — release is for the owner or a workspace admin
//   409  CONVERSATION_ALREADY_ASSIGNED — the race, resolved upstream on a locked row
//   400  not HUMAN status, agent status missing, or at maximum capacity
//   503  routing-audience service unavailable — fails closed
//
// Release is offered whenever a human conversation HAS an owner, without checking
// whether that owner is this agent. CRM has no notion of the acting user here, so
// it cannot pre-empt WeOpen's 403 — and should not, since a workspace admin is
// also allowed to release.
//
// ── BOT CONVERSATIONS HAVE NO ACTION AT ALL ─────────────────────────────────
//
// WeOpen's claim requires HUMAN status, so a BOT conversation cannot be claimed.
// The only BOT acquisition path is the send endpoint's implicit
// `agentInitiatedJoin`, which PR 1 keeps shut. Rather than show a disabled Claim
// button implying a capability that does not exist, this states plainly that
// takeover is not available from CRM yet.
//
// ── CLOSE IS IRREVERSIBLE, SO IT ASKS FIRST ─────────────────────────────────
//
// WeOpen exposes NO reopen endpoint. A close cannot be undone from CRM or anywhere
// else, which is why Close is a two-step action: the second step doubles as the
// confirmation and carries the optional closure summary. That is the whole of the
// extra UX — no resolution taxonomy, no workflow, because the upstream contract
// defines nothing beyond a free-text note.
//
// Close is OWNER-ONLY upstream with no admin or owner role bypass, so an unclaimed
// conversation answers 409 CONVERSATION_NOT_CLAIMED and one owned by someone else
// answers 409 CONVERSATION_ALREADY_ASSIGNED. CRM makes no role decision.
//
// ── AN UNKNOWN OUTCOME MEANS REFETCH, NOT RETRY ─────────────────────────────
//
// Unlike a message send, ownership has no idempotency key and needs none: it is a
// single fact that can be re-read. If a claim times out it may have succeeded, and
// blindly repeating it would answer 409 and look like failure to an agent who
// actually owns the conversation. So the copy says to refresh.

import { useState } from 'react';
import {
  useClaimWeOpenConversationMutation,
  useReleaseWeOpenConversationMutation,
  useCloseWeOpenConversationMutation,
  type WeOpenConversationDetail,
} from '../../api/weopenReadApi';

export type OwnershipOutcome =
  | { kind: 'idle' }
  /** Confirmed refusal from WeOpen. */
  | { kind: 'rejected'; message: string }
  /** Dispatched, no answer. State may already have changed — refetch. */
  | { kind: 'unknown'; message: string };

/** Turn a BFF error into something an agent can act on. */
export function describeOwnershipError(err: unknown): Exclude<OwnershipOutcome, { kind: 'idle' }> {
  const status = (err as { status?: number } | undefined)?.status;
  const data = (err as { data?: { error?: string; message?: string; outcomeUnknown?: boolean } })
    ?.data;
  const code = data?.error;

  if (data?.outcomeUnknown || status === 504 || status === 502) {
    return {
      kind: 'unknown',
      message: 'We could not confirm whether that took effect. Refresh to see the current state.',
    };
  }

  const known: Record<string, string> = {
    CONVERSATION_NOT_CLAIMED: 'Claim this conversation before closing it.',
    CONVERSATION_ALREADY_ASSIGNED: 'Another agent claimed this conversation first.',
    ROUTING_SCOPE_FORBIDDEN: 'This conversation is routed to a different team.',
    NOT_CONVERSATION_OWNER: 'Only the assigned agent or a workspace admin can release this.',
    AUDIENCE_UNAVAILABLE: 'WeOpen could not verify your routing scope right now. Try again shortly.',
  };
  if (code && known[code]) return { kind: 'rejected', message: known[code] };

  // WeOpen returns 400 for capacity and state problems with a usable message, so
  // prefer its own wording over a guess.
  if (status === 400) {
    return { kind: 'rejected', message: data?.message || 'That is not possible for this conversation.' };
  }
  if (status === 403) return { kind: 'rejected', message: 'You do not have access to this conversation.' };
  if (status === 404) return { kind: 'rejected', message: 'This conversation is unavailable.' };
  return { kind: 'rejected', message: 'That action could not be completed.' };
}

export interface WeOpenOwnershipControlsProps {
  conversationId: string;
  conversation: WeOpenConversationDetail | null | undefined;
  /** Called after a confirmed change so the parent refetches authoritative state. */
  onOwnershipChanged: () => void;
}

export default function WeOpenOwnershipControls({
  conversationId,
  conversation,
  onOwnershipChanged,
}: WeOpenOwnershipControlsProps) {
  const [outcome, setOutcome] = useState<OwnershipOutcome>({ kind: 'idle' });
  const [claim, { isLoading: claiming }] = useClaimWeOpenConversationMutation();
  const [release, { isLoading: releasing }] = useReleaseWeOpenConversationMutation();
  const [close, { isLoading: closing }] = useCloseWeOpenConversationMutation();

  /** Two-step close: the confirm step is also where the optional summary is typed. */
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [summary, setSummary] = useState('');

  const status = conversation?.status;
  const hasOwner = !!conversation?.assignedAgentId;

  // Presentation only — WeOpen re-checks all of this.
  const canClaim = status === 'human' && !hasOwner;
  const canRelease = status === 'human' && hasOwner;
  // Same presentation rule as release: an owner must exist. WeOpen still decides
  // whether that owner is the caller.
  const canClose = status === 'human' && hasOwner;
  const isBot = status === 'bot';
  const busy = claiming || releasing || closing;

  async function run(op: 'claim' | 'release') {
    setOutcome({ kind: 'idle' });
    try {
      if (op === 'claim') await claim({ conversationId }).unwrap();
      else await release({ conversationId }).unwrap();
      onOwnershipChanged();
    } catch (err) {
      setOutcome(describeOwnershipError(err));
    }
  }

  /** Only reachable from the confirm step — never from a single click. */
  async function runClose() {
    setOutcome({ kind: 'idle' });
    const note = summary.trim();
    try {
      await close({ conversationId, ...(note ? { summary: note } : {}) }).unwrap();
      setConfirmingClose(false);
      setSummary('');
      onOwnershipChanged();
    } catch (err) {
      // The confirm step stays open so the typed note is not lost.
      setOutcome(describeOwnershipError(err));
    }
  }

  if (isBot) {
    return (
      <div data-testid="weopen-ownership" className="mt-2">
        <p data-testid="weopen-ownership-bot-unavailable" className="text-xs text-gray-500">
          This conversation is handled by the assistant. Taking it over is not available from CRM
          yet — use WeOpen.
        </p>
      </div>
    );
  }

  if (!canClaim && !canRelease && !canClose) return null;

  return (
    <div data-testid="weopen-ownership" className="mt-2">
      <div className="flex items-center gap-2">
        {canClaim && (
          <button
            type="button"
            data-testid="weopen-claim"
            onClick={() => void run('claim')}
            disabled={busy}
            className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:bg-gray-300"
          >
            {claiming ? 'Claiming…' : 'Claim'}
          </button>
        )}

        {canRelease && (
          <button
            type="button"
            data-testid="weopen-release"
            onClick={() => void run('release')}
            disabled={busy}
            className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
          >
            {releasing ? 'Releasing…' : 'Release to queue'}
          </button>
        )}

        {canClose && !confirmingClose && (
          <button
            type="button"
            data-testid="weopen-close"
            onClick={() => setConfirmingClose(true)}
            disabled={busy}
            className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
          >
            Close conversation
          </button>
        )}
      </div>

      {canClose && confirmingClose && (
        <div data-testid="weopen-close-confirm" className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2">
          <p className="text-xs text-gray-600">
            Closing ends this conversation. It cannot be reopened from CRM.
          </p>
          <textarea
            data-testid="weopen-close-summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={2000}
            rows={2}
            placeholder="Optional closure note…"
            className="mt-1 w-full resize-none rounded border border-gray-300 p-1 text-xs focus:border-blue-500 focus:outline-none"
          />
          <div className="mt-1 flex items-center gap-2">
            <button
              type="button"
              data-testid="weopen-close-confirm-submit"
              onClick={() => void runClose()}
              disabled={busy}
              className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:bg-gray-300"
            >
              {closing ? 'Closing…' : 'Confirm close'}
            </button>
            <button
              type="button"
              data-testid="weopen-close-cancel"
              onClick={() => { setConfirmingClose(false); setSummary(''); }}
              disabled={busy}
              className="text-xs font-medium text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {outcome.kind === 'rejected' && (
        <p data-testid="weopen-ownership-error" className="mt-1 text-xs text-red-600">
          {outcome.message}
        </p>
      )}
      {outcome.kind === 'unknown' && (
        <p data-testid="weopen-ownership-unknown" className="mt-1 text-xs text-amber-700">
          {outcome.message}
        </p>
      )}
    </div>
  );
}
